import React, { useEffect, useState, useRef } from 'react';
import { SDK } from '@ringcentral/sdk';
import { Subscriptions } from '@ringcentral/subscriptions';
import dotenv from 'dotenv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faXmark } from "@fortawesome/free-solid-svg-icons";

import styles from '../styles/RingCentralWebSocket.module.css'; // Import modular CSS

dotenv.config();

const RingCentralWebSocket = ({ targetPhoneNumber, setIsModalOpen }) => {
  const [messages, setMessages] = useState([]); // Stores chat messages
  const [newMessage, setNewMessage] = useState(''); // Stores the message to send
  const [imageData, setImageData] = useState({}); // Caches image blobs
  const [enlargedImage, setEnlargedImage] = useState(null); // Stores the image to be enlarged
  const [loadingMessages, setLoadingMessages] = useState(true); // Loading state for messages
  const [loadingImages, setLoadingImages] = useState({}); // Loading state for images
  const chatHistoryRef = useRef(null); // Ref for the chat history container
  const rateLimitRef = useRef({ remaining: Infinity, window: 0, resetTime: 0 }); // Rate limit tracking

  useEffect(() => {
    const rcsdk = new SDK({
      server: process.env.REACT_APP_RC_SERVER_URL,
      clientId: process.env.REACT_APP_RC_APP_CLIENT_ID,
      clientSecret: process.env.REACT_APP_RC_APP_CLIENT_SECRET,
    });

    const platform = rcsdk.platform();
    const subscriptions = new Subscriptions({ sdk: rcsdk });
    let subscription = null; // Define outside functions to track the instance

    let isMounted = true; // Prevent state updates if component unmounts

    async function fetchPastMessages() {
      try {
        await platform.login({ jwt: process.env.REACT_APP_RC_USER_JWT });

        const queryParams = {
          dateFrom: '2023-01-01T00:00:00.000Z',
          messageType: ['SMS'],
          perPage: 1000,
        };

        await handleRateLimit(platform, async () => {
          const response = await platform.get(
            '/restapi/v1.0/account/~/extension/~/message-store',
            queryParams
          );

          const data = await response.json();
          updateRateLimit(response.headers);

          const filteredMessages = data.records.filter(
            (msg) =>
              msg.from.phoneNumber === targetPhoneNumber ||
              msg.to.some((recipient) => recipient.phoneNumber === targetPhoneNumber)
          );

          if (isMounted) {
            setMessages(filteredMessages.reverse());
            setLoadingMessages(false);
          }
        });
      } catch (error) {
        console.error('Failed to fetch past messages:', error);
      }
    }

    async function initializeWebSocket() {
      try {
        if (!subscription) {
          subscription = subscriptions.createSubscription();
        }

        await platform.login({ jwt: process.env.REACT_APP_RC_USER_JWT });

        subscription
          .setEventFilters(['/restapi/v1.0/account/~/extension/~/message-store/instant?type=SMS'])
          .register()
          .then(() => {
            console.log('RingCentral TextMessage WebSocket says: Ready to receive incoming SMS via WebSocket.');

            subscription.on(subscription.events.notification, (evt) => {
              if (
                evt.body.from.phoneNumber === targetPhoneNumber ||
                evt.body.to.some((recipient) => recipient.phoneNumber === targetPhoneNumber)
              ) {
                setMessages((prevMessages) => [...prevMessages, evt.body]);
              }
            });

            subscription.on(subscription.events.removeSuccess, async () => {
              console.warn('RingCentral TextMessage WebSocket says: WebSocket connection lost. Reconnecting...');
              await initializeWebSocket();
            });
          })
          .catch((error) => {
            console.error('RingCentral TextMessage WebSocket says: Subscription failed:', error);
          });
      } catch (error) {
        console.error('RingCentral TextMessage WebSocket says:  WebSocket initialization failed:', error);
      }
    }

    fetchPastMessages().then(initializeWebSocket);

    return () => {
      isMounted = false;
      if (subscription) {
        subscription.remove();
        subscription = null;
      }
      platform.logout();
    };
  }, []);


  const handleRateLimit = async (platform, apiCall) => {
    const now = Date.now();
    const { remaining, resetTime } = rateLimitRef.current;

    if (remaining <= 0 && now < resetTime) {
      const delay = resetTime - now;
      console.log(`Rate limit reached. Retrying in ${delay} ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    await apiCall();
  };

  const updateRateLimit = (headers) => {
    const limit = parseInt(headers.get('X-Rate-Limit-Limit') || '0');
    const remaining = parseInt(headers.get('X-Rate-Limit-Remaining') || '0');
    const window = parseInt(headers.get('X-Rate-Limit-Window') || '0');

    rateLimitRef.current = {
      limit,
      remaining,
      window,
      resetTime: Date.now() + window * 1000,
    };

    console.log(`Rate limit updated: Limit=${limit}, Remaining=${remaining}, Window=${window}s`);
  };

  const fetchImageBlob = async (uri, attachmentId) => {
    const rcsdk = new SDK({
      server: process.env.REACT_APP_RC_SERVER_URL,
      clientId: process.env.REACT_APP_RC_APP_CLIENT_ID,
      clientSecret: process.env.REACT_APP_RC_APP_CLIENT_SECRET,
    });

    const platform = rcsdk.platform();

    setLoadingImages((prev) => ({
      ...prev,
      [attachmentId]: true,
    })); // Set image loading to true when fetching image

    try {
      await platform.login({ jwt: process.env.REACT_APP_RC_USER_JWT });

      const response = await platform.get(uri, null, {
        responseType: 'blob',
      });

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setImageData((prevData) => ({
        ...prevData,
        [attachmentId]: url,
      }));

      setLoadingImages((prev) => ({
        ...prev,
        [attachmentId]: false,
      })); // Set image loading to false once image is fetched
    } catch (error) {
      console.error(`Failed to fetch image for attachment ${attachmentId}:`, error);
      setLoadingImages((prev) => ({
        ...prev,
        [attachmentId]: false,
      })); // Set image loading to false on error
    }
  };

  useEffect(() => {
    messages.forEach((msg) => {
      if (msg.attachments) {
        msg.attachments.forEach((attachment) => {
          if (
            (attachment.contentType === 'image/jpeg' || attachment.contentType === 'image/png') &&
            !imageData[attachment.id]
          ) {
            fetchImageBlob(attachment.uri, attachment.id);
          }
        });
      }
    });
  }, [messages]);

  useEffect(() => {
    if (!loadingMessages && chatHistoryRef.current) {
      // Adding a slight delay to ensure DOM updates
      setTimeout(() => {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
      }, 100);
    }
  }, [messages, loadingMessages]);


  const sendSMS = async () => {
    const rcsdk = new SDK({
      server: process.env.REACT_APP_RC_SERVER_URL,
      clientId: process.env.REACT_APP_RC_APP_CLIENT_ID,
      clientSecret: process.env.REACT_APP_RC_APP_CLIENT_SECRET,
    });

    const platform = rcsdk.platform();

    try {
      await platform.login({ jwt: process.env.REACT_APP_RC_USER_JWT });

      await platform.post('/restapi/v1.0/account/~/extension/~/sms', {
        from: { phoneNumber: process.env.REACT_APP_RC_PHONE_NUMBER },
        to: [{ phoneNumber: targetPhoneNumber }],
        text: newMessage,
      });

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          from: { phoneNumber: process.env.REACT_APP_RC_PHONE_NUMBER },
          to: [{ phoneNumber: targetPhoneNumber }],
          subject: newMessage,
          creationTime: new Date().toISOString(),
        },
      ]);

      setNewMessage('');
    } catch (error) {
      console.error('Failed to send SMS:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const groupedMessages = messages.reduce((acc, message) => {
    const date = formatDate(message.creationTime);
    acc[date] = acc[date] || [];
    acc[date].push(message);
    return acc;
  }, {});

  const handleImageClick = (imageUrl) => {
    setEnlargedImage(imageUrl);
  };

  const handleCloseModal = () => {
    setEnlargedImage(null);
  };

  const handleDownloadImage = (imageUrl) => {
    const link = document.createElement('a');
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = currentDate.toLocaleString('default', { month: 'short' });
    const year = currentDate.getFullYear();
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const seconds = String(currentDate.getSeconds()).padStart(2, '0');

    const timestamp = `${day}-${month}-${year}-${hours}${minutes}${seconds}`;
    link.href = imageUrl;
    link.download = `RC-Img-${timestamp}.jpg`;
    link.click();
  };

  return (
    <div className={styles.chatContainer}>
      <header className={styles.chat_header}>
        <h3>Message {targetPhoneNumber}</h3>
        <button className={styles.close_button} onClick={() => setIsModalOpen(false)}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </header>
      <div className={styles.chatHistory} ref={chatHistoryRef}>

        {loadingMessages ?
          (
            // Show loading spinner while messages are loading
            <div className={styles.spinner_container}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading Messages...</p>
            </div>
          ) :

          (messages.length <= 0 ?

            // Show not found if no messages are found
            (<p className={styles.message_not_found}>No Message history found</p>) :

            // Show messages if messages are found
            (Object.entries(groupedMessages).map(([date, messages]) => (
              <div key={date}>
                <div className={styles.dateLabel}>
                  <p>{date}</p>
                </div>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`${styles.message} ${msg.from.phoneNumber === targetPhoneNumber ? styles.incoming : styles.outgoing
                      } ${msg.attachments && msg.attachments.length > 1 ? styles.mmsMessage : ''}`}
                  >
                    <p>{msg.subject}</p>
                    {msg.attachments &&
                      msg.attachments.map((attachment) =>
                        attachment.contentType === 'image/jpeg' || attachment.contentType === 'image/png' ? (
                          <div key={attachment.id} className={styles.imageWrapper}>
                            {loadingImages[attachment.id] ? (
                              <div className={styles.loadingSpinner}></div> // Show loading spinner
                            ) : (
                              <img
                                src={imageData[attachment.id] || ''}
                                alt="MMS Attachment"
                                className={styles.attachmentImage}
                                onClick={() => handleImageClick(imageData[attachment.id])}
                              />
                            )}
                            {!loadingImages[attachment.id] && (
                              <button
                                className={styles.downloadButton}
                                onClick={() => handleDownloadImage(imageData[attachment.id])}
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </button>
                            )}
                          </div>

                        ) : null
                      )}
                    <small className={styles.timeLabel}>{formatTime(msg.creationTime)}</small>
                  </div>
                ))}
              </div>
            )))
          )
        }

      </div>

      {enlargedImage && (
        <div className={styles.modal} onClick={handleCloseModal}>
          <div className={styles.modalContent}>
            <img src={enlargedImage} alt="Enlarged MMS Attachment" className={styles.enlargedImage} />
          </div>
        </div>
      )}

      <div className={styles.messageInputWrapper}>
        <div className={styles.messageInput}>
          <textarea
            className={styles.textarea}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onInput={(e) => {
              e.target.style.height = '20px'; // Reset height to recalculate
              e.target.style.height = `${e.target.scrollHeight}px`; // Set height based on content
            }}
            placeholder="Type your message here..."
          />
          <button
            className={styles.button}
            onClick={sendSMS}
            disabled={!newMessage.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default RingCentralWebSocket;
